import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { t } from "@impero-com/common/utils/i18n";
import { set, clone } from "@impero-com/common/utils/obj";

import {
    UserId,
    UpdatableControlPrograms,
    StandardAccessRight,
    UserOverview,
} from "api/generated/model";
import {
    BulkAccessRights,
    BulkAccessRightsValues,
} from "components/accessRights/bulk";
import {
    UserAndRights,
    ControlProgramAccessRightsInstance,
    ControlProgramAccessRights,
} from "utils/accessRights/models";
import {
    FormRootComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

interface ManageAccessRightsFormValues {
    userAndRights: BulkAccessRightsValues<ControlProgramAccessRights>;
    patch: Pick<UpdatableControlPrograms, "accessRights">;
}

interface ManageAccessRightsFormProps extends FormRootComponentProps<ManageAccessRightsFormValues> {
    readOnly: boolean;
    initialUserAndRights: Array<
        Array<UserAndRights<ControlProgramAccessRights>>
    >;
}

class ManageAccessRightsForm extends React.PureComponent<ManageAccessRightsFormProps> {
    render = () => {
        const { form, readOnly, initialUserAndRights } = this.props;
        const validators: Array<
            ValidationRule<BulkAccessRightsValues<ControlProgramAccessRights>>
        > = [];
        if (ControlProgramAccessRightsInstance.requiresAdmin) {
            validators.push({
                kind: "custom",
                validator: (value, _rule) => {
                    if (
                        value.userAndRights.some(
                            (uars) =>
                                !uars.some((uar) => uar.accessRight === "Admin")
                        )
                    ) {
                        return t(
                            "AccessRights_User_WithAdminAccess_Validation"
                        );
                    }
                    return undefined;
                },
            });
        }
        return (
            <>
                <Form.Item>
                    {form.getFieldDecorator("userAndRights", {
                        trigger: "", // Update handled by calls to setFieldsValue
                        valuePropName: "values",
                        initialValue: {
                            userAndRights: initialUserAndRights,
                            absentUsers: [],
                        },
                        rules: validationRulesConverter<
                            BulkAccessRightsValues<ControlProgramAccessRights>
                        >()("userAndRights", form, validators),
                    })(
                        !readOnly ? (
                            <BulkAccessRights
                                type="EDIT"
                                label={t("AccessRights_AccessManagement")}
                                resourceAccessRights={
                                    ControlProgramAccessRightsInstance
                                }
                                onChangeUserAccessRight={
                                    this.handleChangeUserAccessRight
                                }
                                onRemoveUserAccessRight={
                                    this.handleRemoveUserAccessRight
                                }
                                onRemoveUser={this.handleRemoveUser}
                            />
                        ) : (
                            <BulkAccessRights
                                type="READ_ONLY"
                                label={t("AccessRights_AccessManagement")}
                                resourceAccessRights={
                                    ControlProgramAccessRightsInstance
                                }
                            />
                        )
                    )}
                </Form.Item>
                <Form.Item>
                    {
                        // This is only used to set the initial patch value.
                        // It ensures it is always defined.
                        form.getFieldDecorator("patch", {
                            trigger: "", // Update handled by calls to setFieldsValue
                            initialValue: {},
                        })(<></>)
                    }
                </Form.Item>
            </>
        );
    };

    private handleChangeUserAccessRight = (
        user: UserOverview,
        accessRight: StandardAccessRight
    ) => {
        const { form } = this.props;
        const values: BulkAccessRightsValues<ControlProgramAccessRights> =
            form.getFieldValue("userAndRights") || { userAndRights: [] };
        const patch = form.getFieldValue("patch") || {};
        let newUserAndRights: Array<
            Array<UserAndRights<ControlProgramAccessRights>>
        >;
        if (values.userAndRights.length > 0) {
            newUserAndRights = values.userAndRights.map((uars) => {
                let seen = false;
                const newUars = uars.map((uar) => {
                    if (uar.user.id === user.id) {
                        seen = true;
                        return set(uar, { accessRight });
                    } else {
                        return uar;
                    }
                });
                if (!seen) {
                    newUars.push({ user, accessRight });
                }
                return newUars;
            });
        } else {
            newUserAndRights = [[{ user, accessRight }]];
        }
        const absentUsers = values.absentUsers || [];
        let newAbsentUsers = absentUsers.filter((u) => u.id !== user.id);
        if (newAbsentUsers.length === absentUsers.length) {
            newAbsentUsers = absentUsers;
        }
        const newPatch: Record<UserId, StandardAccessRight | null> =
            patch.accessRights ? clone(patch.accessRights) : {};
        newPatch[user.id] = accessRight;
        form.setFieldsValue(
            {
                userAndRights: {
                    userAndRights: newUserAndRights,
                    absentUsers: newAbsentUsers,
                },
                patch: set(patch, { accessRights: newPatch }),
            },
            () => {
                form.validateFields(["userAndRights"]);
            }
        );
    };

    private handleRemoveUserAccessRight = (userId: UserId) => {
        return this.handleRemoveUserAccessRightInternal(userId, false);
    };

    private handleRemoveUser = (userId: UserId) => {
        return this.handleRemoveUserAccessRightInternal(userId, true);
    };

    private handleRemoveUserAccessRightInternal = (
        userId: UserId,
        removeUser: boolean
    ) => {
        const { form } = this.props;
        const values: BulkAccessRightsValues<ControlProgramAccessRights> =
            form.getFieldValue("userAndRights") || { userAndRights: [] };
        const patch = form.getFieldValue("patch") || {};
        let removedUser: UserOverview | undefined;
        const newUserAndRights = values.userAndRights.map((uars) => {
            const newUars = uars.filter((uar) => {
                if (uar.user.id === userId) {
                    removedUser = uar.user;
                    return false;
                } else {
                    return true;
                }
            });
            return newUars;
        });
        const absentUsers = values.absentUsers || [];
        let newAbsentUsers: Array<UserOverview>;
        if (!removeUser && removedUser) {
            newAbsentUsers = absentUsers.slice();
            newAbsentUsers.push(removedUser);
        } else if (removeUser) {
            newAbsentUsers = absentUsers.filter((u) => u.id !== userId);
        } else {
            newAbsentUsers = absentUsers;
        }
        const newPatch: Record<UserId, StandardAccessRight | null> =
            patch.accessRights ? clone(patch.accessRights) : {};
        newPatch[userId] = null;
        form.setFieldsValue(
            {
                userAndRights: {
                    userAndRights: newUserAndRights,
                    absentUsers: newAbsentUsers,
                },
                patch: set(patch, { accessRights: newPatch }),
            },
            () => {
                form.validateFields(["userAndRights"]);
            }
        );
    };
}

export { ManageAccessRightsForm, type ManageAccessRightsFormValues };
